/**
 * Email validation utilities for patient intake forms.
 * - Strict format validation (RFC-friendly subset)
 * - Common typo detection for popular Argentine email providers
 *
 * Returns suggestion only when we are reasonably confident
 * (one-edit distance from a known popular domain).
 */

// Most popular email domains in Argentina (based on common usage)
const POPULAR_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "hotmail.com.ar",
  "yahoo.com",
  "yahoo.com.ar",
  "outlook.com",
  "outlook.com.ar",
  "icloud.com",
  "live.com",
  "live.com.ar",
  "fibertel.com.ar",
  "speedy.com.ar",
  "fleni.org.ar",
  "thaleshealth.com",
];

// RFC-friendly format check (simple but covers 99% real cases)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmailFormat(email) {
  if (!email || typeof email !== "string") return false;
  const trimmed = email.trim();
  if (trimmed.length < 5 || trimmed.length > 254) return false;
  return EMAIL_REGEX.test(trimmed);
}

// Levenshtein distance (iterative, O(n*m))
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  return matrix[a.length][b.length];
}

/**
 * Suggests a corrected email if the domain looks like a typo of a popular one.
 * Returns null if no suggestion is needed (domain already valid or too different).
 */
export function suggestEmailCorrection(email) {
  if (!email || typeof email !== "string") return null;
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf("@");
  if (atIndex === -1 || atIndex === trimmed.length - 1) return null;

  const localPart = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);

  if (!localPart) return null;

  // No suggestion if domain is exactly a popular one
  if (POPULAR_DOMAINS.includes(domain)) return null;

  // Find closest popular domain within edit distance threshold
  let bestDomain = null;
  let bestDistance = Infinity;

  for (const popular of POPULAR_DOMAINS) {
    const distance = levenshtein(domain, popular);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestDomain = popular;
    }
  }

  // Only suggest when very close (1 or 2 edits) and domain has reasonable length
  // Distance 1: gmial.com -> gmail.com, gmail.con -> gmail.com
  // Distance 2: gmaill.con -> gmail.com
  const maxDistance = domain.length <= 6 ? 1 : 2;

  if (bestDistance > 0 && bestDistance <= maxDistance && bestDomain) {
    return `${localPart}@${bestDomain}`;
  }

  return null;
}
